import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Search,
  MapPin,
  Phone,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  Loader2,
  RefreshCw,
  Building2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '../ui/card';
import { getEnabledTrades, type Trade } from '../../config/trades';
import { isValidUKPostcode } from '../../utils/postcodeUtils';
import { searchPlaces, claimBusiness, verifyBusiness, type PlacesSearchResult } from '../../services/locksmiths';
import { useSupabaseAuth } from '../../hooks/auth/useSupabaseAuth';

type WizardStep = 'trade' | 'postcode' | 'results' | 'verification' | 'otp' | 'success';

interface ClaimBusinessWizardProps {
  onComplete: () => Promise<void>;
}

export default function ClaimBusinessWizard({ onComplete }: ClaimBusinessWizardProps) {
  const { user } = useSupabaseAuth();
  const [step, setStep] = useState<WizardStep>('trade');
  const [selectedTrade, setSelectedTrade] = useState<Trade | null>(null);
  const [postcode, setPostcode] = useState('');
  const [results, setResults] = useState<PlacesSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Claim state
  const [claimingId, setClaimingId] = useState<number | null>(null);
  const [claimingPlaceId, setClaimingPlaceId] = useState<string | null>(null);
  const [claimLoading, setClaimLoading] = useState(false);
  const [phoneLastFour, setPhoneLastFour] = useState('');
  const [channel, setChannel] = useState<'sms' | 'call'>('sms');
  const [businessName, setBusinessName] = useState('');
  const [businessId, setBusinessId] = useState<number | null>(null);

  // OTP state
  const [otpCode, setOtpCode] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [devToken, setDevToken] = useState<string | null>(null);

  const enabledTrades = getEnabledTrades();

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSelectTrade = (trade: Trade) => {
    setSelectedTrade(trade);
    setStep('postcode');
  };

  const handleSearch = async () => {
    if (!selectedTrade || !postcode.trim()) return;

    if (!isValidUKPostcode(postcode)) {
      setSearchError('Please enter a valid UK postcode');
      return;
    }

    setSearching(true);
    setSearchError(null);

    try {
      const response = await searchPlaces({
        trade: selectedTrade.serviceTypeValue,
        postcode: postcode.trim()
      });
      setResults(response.results);
      setStep('results');
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleClaim = async (result: PlacesSearchResult) => {
    if (!user || !selectedTrade) return;

    setClaimingId(result.id || null);
    setClaimingPlaceId(result.google_place_id);
    setClaimLoading(true);

    try {
      const response = await claimBusiness({
        businessId: result.id,
        googlePlaceId: result.google_place_id || undefined,
        authUserId: user.id,
        trade: selectedTrade.serviceTypeValue
      });

      setPhoneLastFour(response.phoneLastFour);
      setChannel(response.channel);
      setBusinessName(response.businessName);
      setBusinessId(response.businessId);
      setDevToken(response.dev_token || null);
      setStep('verification');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to start claim');
    } finally {
      setClaimLoading(false);
      setClaimingId(null);
      setClaimingPlaceId(null);
    }
  };

  const handleVerify = async () => {
    if (!user || !businessId || otpCode.length !== 6) return;

    setVerifyLoading(true);
    setVerifyError(null);

    try {
      await verifyBusiness({
        code: otpCode,
        businessId,
        authUserId: user.id
      });
      setStep('success');
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || !user || !selectedTrade) return;
    setResendCooldown(60);

    try {
      const response = await claimBusiness({
        businessId: businessId || undefined,
        googlePlaceId: claimingPlaceId || undefined,
        authUserId: user.id,
        trade: selectedTrade.serviceTypeValue
      });
      setDevToken(response.dev_token || null);
      toast.success('New code sent');
    } catch (err) {
      toast.error('Failed to resend code');
    }
  };

  const handleComplete = async () => {
    await onComplete();
  };

  // Auto-submit OTP when 6 digits entered
  useEffect(() => {
    if (otpCode.length === 6 && !verifyLoading) {
      handleVerify();
    }
  }, [otpCode]);

  return (
    <div className="max-w-2xl mx-auto mt-8 px-4">
      {/* Step: Select Trade */}
      {step === 'trade' && (
        <div>
          <div className="text-center mb-8">
            <Building2 className="h-12 w-12 text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Claim Your Business</h2>
            <p className="text-gray-600">
              Select your trade to find your business on Google Places and verify ownership.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {enabledTrades.map((trade) => (
              <Card
                key={trade.slug}
                className="cursor-pointer hover:border-primary hover:shadow-md transition-all"
                onClick={() => handleSelectTrade(trade)}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">{trade.label}</CardTitle>
                  <CardDescription>
                    {trade.subServices.length > 0
                      ? trade.subServices.map(s => s.label).join(', ')
                      : trade.pluralLabel}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="outline" className="w-full">
                    Select <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Step: Enter Postcode */}
      {step === 'postcode' && (
        <div>
          <button
            onClick={() => setStep('trade')}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to trade selection
          </button>

          <div className="text-center mb-8">
            <MapPin className="h-12 w-12 text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Find Your Business</h2>
            <p className="text-gray-600">
              Enter your business postcode to search for your {selectedTrade?.label} listing.
            </p>
          </div>

          <div className="max-w-sm mx-auto space-y-4">
            <Input
              value={postcode}
              onChange={(e) => {
                setPostcode(e.target.value.toUpperCase());
                setSearchError(null);
              }}
              placeholder="e.g. M1 5AN"
              className="text-center text-lg"
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />

            {searchError && (
              <p className="text-sm text-red-600 text-center">{searchError}</p>
            )}

            <Button
              onClick={handleSearch}
              disabled={searching || !postcode.trim()}
              className="w-full"
            >
              {searching ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="mr-2 h-4 w-4" />
                  Search
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Step: Search Results */}
      {step === 'results' && (
        <div>
          <button
            onClick={() => setStep('postcode')}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to search
          </button>

          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-1">
              {results.length} {selectedTrade?.pluralLabel || 'businesses'} found
            </h2>
            <p className="text-gray-600">
              Near {postcode} &middot; Select your business to claim it
            </p>
          </div>

          {results.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No businesses found</h3>
              <p className="text-gray-600 mb-4">
                Try a different postcode or expand your search area.
              </p>
              <Button variant="outline" onClick={() => setStep('postcode')}>
                Try another postcode
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {results.map((result, index) => (
                <div
                  key={result.google_place_id || `result-${index}`}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {result.company_name}
                      </h3>
                      {result.address && (
                        <p className="text-sm text-gray-600 mt-1 truncate">{result.address}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
                        {result.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {result.phone}
                          </span>
                        )}
                        {result.postcode && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {result.postcode}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex-shrink-0">
                      {result.is_claimed ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
                          Claimed
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handleClaim(result)}
                          disabled={claimLoading}
                        >
                          {claimLoading &&
                           (claimingId === result.id || claimingPlaceId === result.google_place_id) ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Claim'
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step: Verification Sent */}
      {step === 'verification' && (
        <div className="text-center">
          <div className="mb-8">
            <Phone className="h-12 w-12 text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Verify Your Business</h2>
            <p className="text-gray-600">
              {channel === 'call'
                ? `We are calling the phone number ending in ${phoneLastFour} with your verification code.`
                : `We have sent a verification code via SMS to the phone number ending in ${phoneLastFour}.`}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              This is the phone number listed on the Google Places profile for <strong>{businessName}</strong>.
            </p>
          </div>

          <Button onClick={() => setStep('otp')} className="w-full max-w-xs">
            Enter Verification Code <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Step: Enter OTP */}
      {step === 'otp' && (
        <div>
          <button
            onClick={() => setStep('verification')}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </button>

          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2">Enter Verification Code</h2>
            <p className="text-gray-600">
              Enter the 6-digit code sent to ****{phoneLastFour}
            </p>
          </div>

          <div className="max-w-xs mx-auto space-y-4">
            <Input
              value={otpCode}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                setOtpCode(val);
                setVerifyError(null);
              }}
              placeholder="000000"
              className="text-center text-2xl tracking-[0.5em] font-mono"
              maxLength={6}
              inputMode="numeric"
              autoFocus
              disabled={verifyLoading}
            />

            {devToken && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm">
                <strong>Dev Token:</strong> {devToken}
              </div>
            )}

            {verifyError && (
              <p className="text-sm text-red-600 text-center">{verifyError}</p>
            )}

            <Button
              onClick={handleVerify}
              disabled={verifyLoading || otpCode.length !== 6}
              className="w-full"
            >
              {verifyLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify'
              )}
            </Button>

            <div className="text-center">
              <p className="text-sm text-gray-500 mb-2">Didn't receive the code?</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResend}
                disabled={resendCooldown > 0 || verifyLoading}
              >
                <RefreshCw className="h-3 w-3 mr-2" />
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step: Success */}
      {step === 'success' && (
        <div className="text-center">
          <div className="mb-8">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Business Claimed!</h2>
            <p className="text-gray-600">
              You have successfully claimed <strong>{businessName}</strong>.
              You can now manage your business listing from the dashboard.
            </p>
          </div>

          <Button onClick={handleComplete} className="w-full max-w-xs">
            Go to Dashboard
          </Button>
        </div>
      )}
    </div>
  );
}
